import { useEffect, useRef, useState, useCallback } from "react";
import { RootState } from "@/components/state/store";
import { useDispatch, useSelector } from "react-redux";
import { setAppVersion } from "@/components/state/version/versionSlice";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useIsAuthenticated } from "react-auth-kit";
import { useSnackbar } from "notistack";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import GenericNotification from "@/components/notification/genericNotification";
import GenericConfirmationModal from "@/components/forms/comfirmations/genericConfirmationModal";
import RefreshIcon from "@mui/icons-material/Refresh";
import { prefetchNextAssets, fetchVersion } from "@/version/versionWatcherUtils";
import { useUpdateAvailableNotification } from "@/context/updateAvailableNotificationContext";
import { useUnsavedChanges } from "@/context/unsavedChangesContext";
import styles from "@/version/versionWatcher.module.css";

export default function VersionWatcher(): JSX.Element | null {
  const { t } = useTranslation();
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const [showNotification, setShowNotification] = useState(false);
  const [showUpdateConfirmation, setShowUpdateConfirmation] = useState(false);
  const [newVersionDetectedAt, setNewVersionDetectedAt] = useState<
    number | null
  >(null);
  const [previousPathname, setPreviousPathname] = useState<string>(
    location.pathname
  );
  const snackbarKeyRef = useRef<any>(null);
  const bcRef = useRef<BroadcastChannel | null>(null);
  const dispatch = useDispatch();
  const appVersion = useSelector(
    (state: RootState) => state.appVersion?.value ?? null
  );
  const { setShowUpdateIcon, setUpdateIconHandlers } =
    useUpdateAvailableNotification();
  const { hasUnsavedChanges } = useUnsavedChanges();
  const appVersionRef = useRef<string | null>(appVersion);
  const newVersionDetectedAtRef = useRef<number | null>(newVersionDetectedAt);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const isOnStartPageRef = useRef<() => boolean>(() => false);
  const handleReloadRef = useRef<() => Promise<void>>(() => Promise.resolve());
  const { closeSnackbar } = useSnackbar();

  const SHOW_UPDATE_ICON_AFTER = 1 * 60 * 1000; // 1 minute
  const POLLING_INTERVAL = 5 * 60 * 1000; // 5 minutes
  const FORCE_SHOW_SNACKBAR_NOTIFICATION_AFTER = 4 * 60 * 60 * 1000; // 4 hours
  const SKIP_KEY = "app_version_skip_once";
  const LEGACY_PURGE_AFTER_RELOAD_KEY = "app_version_purge_after_reload";
  const CHANNEL_NAME = "app-version";

  useEffect(() => {
    appVersionRef.current = appVersion;
  }, [appVersion]);

  useEffect(() => {
    newVersionDetectedAtRef.current = newVersionDetectedAt;
  }, [newVersionDetectedAt]);

  const markNewVersionDetected = useCallback(() => {
    if (!newVersionDetectedAtRef.current) {
      const detectedAt = Date.now();
      newVersionDetectedAtRef.current = detectedAt;
      setNewVersionDetectedAt(detectedAt);
    }
  }, []);

  const isOnLoginScreen = useCallback(() => {
    const pathname = location.pathname;
    return (
      pathname === "/login" ||
      pathname.startsWith("/login/") ||
      pathname === "/login/reminder/"
    );
  }, [location.pathname]);

  const isOnStartPage = useCallback(() => {
    const pathname = location.pathname;
    return pathname === "/" || pathname === "";
  }, [location.pathname]);

  const handleReload = useCallback(async () => {
    if (hasUnsavedChanges) {
      setShowNotification(false);
      setShowUpdateIcon(false);
      setShowUpdateConfirmation(false);
      return;
    }

    try {
      sessionStorage.setItem(SKIP_KEY, "1");
    } catch {
      // ignore
    }

    setShowUpdateIcon(false);
    setShowNotification(false);
    setNewVersionDetectedAt(null);

    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      /* no-op */
    }

    try {
      if ("caches" in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch {
      /* no-op */
    }

    const url = new URL(window.location.href);
    url.searchParams.set("_cb", Date.now().toString());
    window.location.replace(url.toString());
  }, [hasUnsavedChanges, setShowUpdateIcon]);

  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    isOnStartPageRef.current = isOnStartPage;
  }, [isOnStartPage]);

  useEffect(() => {
    handleReloadRef.current = handleReload;
  }, [handleReload]);

  useEffect(() => {
    try {
      if ("BroadcastChannel" in window) {
        bcRef.current = new BroadcastChannel(CHANNEL_NAME);
        bcRef.current.onmessage = async (e: MessageEvent) => {
          try {
            if (e.data?.type === "new-version") {
              markNewVersionDetected();

              const onStartPage = isOnStartPage();
              // const onLoginScreen = isOnLoginScreen();
              // console.log(`Broadcast new-version: pathname=${location.pathname}, onStartPage=${onStartPage}, onLoginScreen=${onLoginScreen}`);

              if (onStartPage) {
                // console.log("Auto-reloading from start page (broadcast)");
                void prefetchNextAssets();
                setTimeout(() => {
                  handleReload();
                }, 100);
                return;
              }

              if (!isAuthenticated() && !hasUnsavedChanges) {
                setShowNotification(true);
              }
              void prefetchNextAssets();
            }
            if (e.data?.type === "reload-now") {
              await handleReload();
            }
          } catch {
            // ignore
          }
        };
      }
    } catch {
      // ignore
    }
    return () => {
      try {
        bcRef.current?.close();
      } catch {
        // ignore
      }
    };
  }, [
    location.pathname,
    isOnStartPage,
    isOnLoginScreen,
    isAuthenticated,
    hasUnsavedChanges,
    handleReload,
    markNewVersionDetected,
  ]);

  useEffect(() => {
    if (hasUnsavedChanges) {
      setShowNotification(false);
      setShowUpdateIcon(false);
      setShowUpdateConfirmation(false);
      if (snackbarKeyRef.current) {
        closeSnackbar(snackbarKeyRef.current);
        snackbarKeyRef.current = null;
      }
    }
  }, [hasUnsavedChanges, setShowUpdateIcon, closeSnackbar]);

  useEffect(() => {
    let cancelled = false;
    let skipFirstCheck = false;
    try {
      skipFirstCheck = sessionStorage.getItem(SKIP_KEY) === "1";
      if (skipFirstCheck) sessionStorage.removeItem(SKIP_KEY);
    } catch {
      // ignore
    }

    try {
      sessionStorage.removeItem(LEGACY_PURGE_AFTER_RELOAD_KEY);
    } catch {
      // ignore
    }

    const initialCheck = async () => {
      if (skipFirstCheck) {
        skipFirstCheck = false;
        return;
      }
      const current = await fetchVersion();
      if (cancelled || !current) return;

      const cached = appVersionRef.current;
      if (!cached) {
        appVersionRef.current = current;
        dispatch(setAppVersion(current));
        return;
      }

      if (cached !== current) {
        appVersionRef.current = current;
        dispatch(setAppVersion(current));

        markNewVersionDetected();

        const onStartPage = isOnStartPageRef.current();
        // const onLoginScreen = isOnLoginScreen();
        // console.log(`Direct version check: pathname=${location.pathname}, onStartPage=${onStartPage}, onLoginScreen=${onLoginScreen}`);

        if (onStartPage) {
          // console.log("Auto-reloading from start page (direct check)");
          void prefetchNextAssets();
          setTimeout(() => {
            void handleReloadRef.current();
          }, 100);
          return;
        }

        if (
          !isAuthenticatedRef.current() &&
          !hasUnsavedChangesRef.current
        ) {
          setShowNotification(true);
        }
        void prefetchNextAssets();
        try {
          bcRef.current?.postMessage({ type: "new-version" });
        } catch {
          /* no-op */
        }
      }
    };

    initialCheck();

    const id = window.setInterval(initialCheck, POLLING_INTERVAL);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [
    dispatch,
    markNewVersionDetected,
  ]);

  useEffect(() => {
    if (
      newVersionDetectedAt &&
      !hasUnsavedChanges &&
      !isOnLoginScreen() &&
      !isOnStartPage()
    ) {
      const timeSinceDetection = Date.now() - newVersionDetectedAt;
      const timeUntilSnackbar =
        FORCE_SHOW_SNACKBAR_NOTIFICATION_AFTER - timeSinceDetection;

      if (timeUntilSnackbar <= 0) {
        if (isAuthenticated()) {
          setShowNotification(true);
        }
      } else {
        const timer = setTimeout(() => {
          if (isAuthenticated() && !hasUnsavedChanges && !isOnLoginScreen()) {
            setShowNotification(true);
          }
        }, timeUntilSnackbar);

        return () => clearTimeout(timer);
      }
    }
  }, [
    newVersionDetectedAt,
    hasUnsavedChanges,
    isAuthenticated,
    isOnLoginScreen,
    isOnStartPage,
  ]);

  useEffect(() => {
    if (
      newVersionDetectedAt &&
      !hasUnsavedChanges &&
      isAuthenticated() &&
      !isOnLoginScreen() &&
      !isOnStartPage()
    ) {
      const timeSinceDetection = Date.now() - newVersionDetectedAt;
      const timeUntilShowIcon = SHOW_UPDATE_ICON_AFTER - timeSinceDetection;

      if (timeUntilShowIcon <= 0) {
        setShowUpdateIcon(true);
      } else {
        const timer = setTimeout(() => {
          if (
            isAuthenticated() &&
            !hasUnsavedChanges &&
            !isOnLoginScreen() &&
            !isOnStartPage()
          ) {
            setShowUpdateIcon(true);
          }
        }, timeUntilShowIcon);

        return () => clearTimeout(timer);
      }
    }
  }, [
    newVersionDetectedAt,
    hasUnsavedChanges,
    isAuthenticated,
    isOnLoginScreen,
    isOnStartPage,
    setShowUpdateIcon,
  ]);

  useEffect(() => {
    if (
      !hasUnsavedChanges &&
      isOnStartPage() &&
      newVersionDetectedAt &&
      !isOnLoginScreen()
    ) {
      // console.log("Navigated to start page with pending update - auto-reloading");
      void prefetchNextAssets();
      setTimeout(() => {
        handleReload();
      }, 100);
    }
  }, [
    location.pathname,
    isOnStartPage,
    isOnLoginScreen,
    newVersionDetectedAt,
    hasUnsavedChanges,
    handleReload,
  ]);

  useEffect(() => {
    if (
      location.pathname !== previousPathname &&
      newVersionDetectedAt &&
      !hasUnsavedChanges &&
      !isOnLoginScreen() &&
      !isOnStartPage()
    ) {
      // console.log(`Navigated from ${previousPathname} to ${location.pathname} with pending update - auto-reloading`);
      void prefetchNextAssets();
      setTimeout(() => {
        handleReload();
      }, 100);
    }

    setPreviousPathname(location.pathname);
  }, [
    location.pathname,
    previousPathname,
    isOnLoginScreen,
    isOnStartPage,
    newVersionDetectedAt,
    hasUnsavedChanges,
    handleReload,
  ]);

  const handleCloseSnackbar = () => {
    setShowNotification(false);
  };

  const handleConfirmUpdate = () => {
    setShowUpdateConfirmation(false);
    handleReload();
  };

  const handleCancelUpdate = () => {
    setShowUpdateConfirmation(false);
  };

  const handleUpdateIconClick = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUpdateIcon(false);
      return;
    }

    setShowUpdateConfirmation(true);
  }, [hasUnsavedChanges, setShowUpdateIcon]);

  useEffect(() => {
    setUpdateIconHandlers({
      onHover: () => {
        /* no-op */
      },
      onClick: handleUpdateIconClick,
      onMouseLeave: () => {
        /* no-op */
      },
    });
  }, [handleUpdateIconClick, setUpdateIconHandlers]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("_cb")) {
      url.searchParams.delete("_cb");
      window.history.replaceState(
        {},
        "",
        url.pathname +
        (url.search ? "?" + url.searchParams.toString() : "") +
        url.hash
      );
    }
  }, []);

  return (
    <>
      {showNotification && !hasUnsavedChanges && !isOnLoginScreen() && (
        <GenericNotification
          message={t("new_version_available")}
          variant="info"
          persist={true}
          handleCloseSnackbar={handleCloseSnackbar}
          snackbarKeyRef={snackbarKeyRef}
          additionalContent={() => (
            <GenericButton
              onClick={handleReload}
              className={styles.reloadButton}
              startIcon={<RefreshIcon />}
            >
              {t("reload")}
            </GenericButton>
          )}
        />
      )}

      <GenericConfirmationModal
        isOpen={showUpdateConfirmation}
        title={t("update_available")}
        message={t("new_version_available")}
        confirmButtonText={t("reload")}
        cancelButtonText={t("cancel")}
        onConfirm={handleConfirmUpdate}
        onCancel={handleCancelUpdate}
      />
    </>
  );
}

/* NOTE:  Quick Testing the VersionWatcher:

Tip: uncomment the console.log statements to see the flow of the VersionWatcher.
Then, you can test the VersionWatcher by any of the following methods:

Method 1: In the browser console, run:
  new BroadcastChannel('app-version').postMessage({ type: 'new-version' });
PS: You might need to enable "allow pasting" in the browser console.

Method 2: Directly edit the appVersion value in the devtool Application tab.
Then either wait for the polling to trigger the notification, or manually reload the page.
*/
